import { Link, useLocation } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { useTheme } from "@/contexts/ThemeContext";

const Footer = () => {
  const location = useLocation();
  const { pageStyles } = useTheme();
  
  // Get current page theme
  const getCurrentPageName = () => {
    const path = location.pathname;
    if (path === '/') return 'home';
    if (path === '/map') return 'map';
    if (path === '/dive-centers' || path.includes('/dive-center')) return 'dive_centers';
    if (path === '/packages') return 'packages';
    if (path === '/gallery') return 'gallery';
    if (path === '/reviews') return 'reviews';
    if (path === '/contact') return 'contact';
    return 'home'; // fallback
  };
  
  const currentPage = getCurrentPageName();
  const footerTheme = pageStyles[currentPage]?.footer;
  
  // Apply theme or use defaults
  const footerBgValue = footerTheme?.background_value || "#0f172a";
  const footerTextColor = footerTheme?.text_color || "#ffffff";
  const footerAccent = footerTheme?.accent_color || "#22d3ee";
  const footerAccentSecondary = footerTheme?.accent_color_secondary || footerAccent;
  const footerBorderColor = footerTheme?.border_color || "rgba(255,255,255,0.1)";
  
  // Support gradient backgrounds
  const isGradient = footerBgValue.includes('gradient');
  const footerStyle = {
    ...(isGradient ? { background: footerBgValue } : { backgroundColor: footerBgValue }),
    color: footerTextColor
  };
  
  return (
    <footer className="py-12" style={footerStyle}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Top section with logo and links */}
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            {/* Logo and tagline */}
            <div className="text-center md:text-left">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-4">
                <img 
                  src="/george-dives-logo.webp" 
                  alt="George Dives Logo" 
                  className="h-12 w-12 rounded-full object-cover"
                />
                <span className="text-xl font-bold" style={{ color: footerTextColor }}>George Dives</span>
              </div>
              <p className="text-sm" style={{ color: footerTextColor, opacity: 0.7 }}>
                Bringing you the best diving experiences around the world
              </p>
            </div>

            {/* Quick Links */}
            <div className="text-center md:text-left">
              <h3 className="font-semibold mb-4" style={{ color: footerTextColor }}>Quick Links</h3>
              <ul className="space-y-2 text-sm" style={{ color: footerTextColor, opacity: 0.7 }}>
                <li>
                  <Link to="/" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Home</Link>
                </li>
                <li>
                  <Link to="/packages" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Packages</Link>
                </li>
                <li>
                  <Link to="/map" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Map</Link>
                </li>
                <li>
                  <Link to="/gallery" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Gallery</Link>
                </li>
                <li>
                  <Link to="/reviews" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Reviews</Link>
                </li>
                <li>
                  <Link to="/contact" className="transition-colors hover:opacity-100" style={{ color: footerTextColor }} onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = footerTextColor}>Contact</Link>
                </li>
              </ul>
            </div>

            {/* Legal & Contact */}
            <div className="text-center md:text-left">
              <h3 className="font-semibold mb-4" style={{ color: footerTextColor }}>Legal & Contact</h3>
              <ul className="space-y-2 text-sm mb-4" style={{ color: footerTextColor, opacity: 0.7 }}>
                <li>
                  <Link to="/terms" className="transition-colors" onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = ''}>Terms & Conditions</Link>
                </li>
                <li>
                  <Link to="/privacy" className="transition-colors" onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = ''}>Privacy Policy</Link>
                </li>
                <li>
                  <Link to="/accessibility" className="transition-colors" onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = ''}>Accessibility</Link>
                </li>
                <li>
                  <button 
                    onClick={() => {
                      localStorage.removeItem('cookieConsent');
                      localStorage.removeItem('cookieConsentDate');
                      window.location.reload();
                    }}
                    className="transition-colors text-left"
                    onMouseEnter={(e) => e.target.style.color = footerAccent}
                    onMouseLeave={(e) => e.target.style.color = ''}
                  >
                    Manage Cookies
                  </button>
                </li>
                <li>
                  <Link to="/privacy#do-not-sell" className="transition-colors" onMouseEnter={(e) => e.target.style.color = footerAccent} onMouseLeave={(e) => e.target.style.color = ''}>
                    Do Not Sell My Personal Information
                  </Link>
                </li>
              </ul>
              <div className="text-sm" style={{ color: footerTextColor, opacity: 0.7 }}>
                <p>Email: info@georgedives.com</p>
                <p>WhatsApp: +30 698 794 6278</p>
              </div>
              
              {/* Social Media Links */}
              <div className="mt-4">
                <h4 className="font-semibold mb-3" style={{ color: footerTextColor }}>Follow Us</h4>
                <div className="flex gap-3 justify-center md:justify-start">
                  <a
                    href="https://www.instagram.com/george_dives"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{ backgroundColor: footerBorderColor }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = footerAccentSecondary + '30'; e.currentTarget.style.color = footerAccent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = footerBorderColor; e.currentTarget.style.color = ''; }}
                    aria-label="Instagram"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                  <a
                    href="https://www.facebook.com/share/1Q8HzKKNWL/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full flex items-center justify-center transition-all duration-200"
                    style={{ backgroundColor: footerBorderColor }}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = footerAccentSecondary + '30'; e.currentTarget.style.color = footerAccent; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = footerBorderColor; e.currentTarget.style.color = ''; }}
                    aria-label="Facebook"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>

          <Separator className="my-6" style={{ backgroundColor: footerBorderColor }} />
          
          {/* Bottom section */}
          <div className="text-center text-sm" style={{ color: footerTextColor, opacity: 0.6 }}>
            <p>© {new Date().getFullYear()} George Dives. All rights reserved.</p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
